import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useLayoutEffect,
  useState,
} from "react"
import { MOBILEVIEW } from "shared/constants/media"
import { Orientation, RozyLayout } from "shared/constants/views"
import useGetViewportSize from "shared/hooks/useGetViewportSize"

/*
 * Indicates which layout the app should currently be using.
 * Dependent on ChannelContext (for aspect ratio) & ViewportSize
 */

interface LayoutProps {
  children?: ReactNode
}

interface LayoutState {
  layout: RozyLayout
  orientation: Orientation
}

export const LayoutContext = createContext<LayoutState | undefined>(
  undefined,
)

const LayoutProvider: FC<LayoutProps> = ({ children }) => {
  const [layout, setLayout] = useState<RozyLayout>()
  const [orientation, setOrientation] = useState<Orientation>()
  const { viewportWidth, viewportHeight } = useGetViewportSize()

  useLayoutEffect(() => {
    const isMobileWidth = viewportWidth <= MOBILEVIEW
    const viewportAspectRatio = viewportWidth / viewportHeight

    // We check for a minimum aspect-ratio of 4/3 as a condition for landscape,
    // to ensure we don't enter landscape layout when an Android keyboard makes the viewport smaller
    setOrientation(
      viewportAspectRatio > 4 / 3
        ? Orientation.LANDSCAPE
        : Orientation.PORTRAIT,
    )

    if (isMobileWidth) {
      setLayout(RozyLayout.MOBILE)
    } else {
      setLayout(RozyLayout.DESKTOP)
    }
  }, [viewportWidth, viewportHeight])

  const state: LayoutState = {
    layout,
    orientation,
  }

  return (
    <LayoutContext.Provider value={state}>
      {children}
    </LayoutContext.Provider>
  )
}

const useLayoutContext = () => {
  const layoutContext = useContext(LayoutContext)
  if (!layoutContext) {
    throw new Error(
      "No LayoutContext.Provider found when calling useLayoutContext.",
    )
  }
  return layoutContext
}

export { LayoutProvider, useLayoutContext }
